import React, {useEffect, useMemo, useState} from 'react';
import VStack from '../../../../../common/Layout/VStack';
import {useSelector} from 'react-redux';
import {t} from '../../../../../../services/i18n';
import AppText from '../../../../../common/AppText';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import {Colors} from '../../../../../../styles/Colors';
import AdvanceSelect from '../../../../../common/AdvanceSelect';
import FormInput from '../../../../../common/FormInput';
import {dbDateFormat, handleTrackNameBlur} from '../../../../../../services/Helpers';
import {checkRequired} from '../../../../../../services/stepperService';
import SharedTimepicker from '../../../../../common/SharedTimepicker';
import moment from 'moment';
import {HStack} from "native-base";
import SimpleInput from "../../../../../common/SimpleInput";
import CustomButtonV2 from "../../../../../common/CustomButtonV2";
import {GlobalConsts} from "../../../../../../styles/GlobalConsts";

const TrackDetails = ({values, setFieldValue, errors, touched, target, validationObject}) => {
    const box = useSelector(state => state.box.box);
    const isEdit = useSelector(state => !!state.stepper.editType);
    const [nameWarning, setNameWarning] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const trackId = values?.id ?? target?.id ?? null;

    const locations = useMemo(() => box?.locations_box?.map(location => ({
        ...location,
        label: location.location,
        value: location.id
    })) ?? [], [box]);

    const showLocation = locations.length > 1;

    useEffect(() => {
        if (box?.locations_box?.length === 1 && !values.locations_box_fk) {
            setFieldValue('locations_box_fk', box.locations_box[0].id);
        }
    }, [box, values.locations_box_fk, setFieldValue]);

    useEffect(() => {
        if (!isInitialized && values?.name?.length >= 2) {
            handleTrackNameBlur(values.name, isEdit ? trackId : null, setNameWarning);
            setIsInitialized(true);
        }
    }, [values?.name, isInitialized, isEdit, trackId]);

    const onNameChange = (val) => {
        setFieldValue('name', val);
        setNameWarning(false);
        if (!val) {
            return;
        }
        if (!errors.name || errors.name === t('thisFieldIsMandatory')) {
            handleTrackNameBlur(val, isEdit ? trackId : null, setNameWarning);
        }
    };

    const onDateChange = (name, value) => {
        setFieldValue(name, moment(value).format(dbDateFormat));
    };

    return (
        <VStack space={'1rem'} flex={1}>
            <VStack space={'0.2rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('trackName')}{checkRequired('name', validationObject)}</AppText>
                <FormInput
                    value={values.name}
                    onChangeCallback={onNameChange}
                    error={touched.name && errors.name}
                    containerProps={{mb: '0'}}
                    width={'100%'}
                    placeholder={t('trackNameHint')}
                />
                {nameWarning &&
                    <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error} mediumFont>
                        {t('trackNameAlreadyExists')}
                    </AppText>
                }
            </VStack>
            <VStack space={'0.2rem'}>
                <HStack justifyContent={'space-between'} alignItems={'center'}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('description')}</AppText>
                    <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={values.bio.length > 50 ? Colors.error : Colors.newDesign.mediumGrey} mediumFont>{values.bio.length}/50</AppText>
                </HStack>
                <SimpleInput value={values.bio} onChangeCallback={val => setFieldValue('bio', (val ?? '').slice(0, 50))} status={touched.bio && errors.bio ? 'error' : null} inputType={'multiline'}/>
                {touched.bio && errors.bio && <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error} mediumFont>{errors.bio}</AppText>}
            </VStack>

            <VStack space={'0.2rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('start-date')}{checkRequired('start_date', validationObject)}</AppText>
                <SharedTimepicker
                    type={'date'}
                    value={values.start_date ? moment(values.start_date, dbDateFormat) : moment()}
                    onChange={(date) => onDateChange('start_date', date)}
                    textStyle={{fontSize: GlobalStyleAttributes.fonts.standard16}}
                    isError={touched.start_date && errors.start_date}
                />
                {touched.start_date && errors.start_date && <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error} mediumFont>{errors.start_date}</AppText>}
            </VStack>

            <VStack space={'0.2rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('end-date')}{checkRequired('end_date', validationObject)}</AppText>
                <SharedTimepicker
                    type={'date'}
                    value={values.end_date ? moment(values.end_date, dbDateFormat) : null}
                    onChange={(date) => onDateChange('end_date', date)}
                    textStyle={{fontSize: GlobalStyleAttributes.fonts.standard16}}
                    isError={touched.end_date && errors.end_date}
                    min={values.start_date ?? moment().format(dbDateFormat)}
                />
                {touched.end_date && errors.end_date && <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error} mediumFont>{errors.end_date}</AppText>}
            </VStack>

            {showLocation &&
                <VStack space={'0.2rem'}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('location')}{checkRequired('locations_box_fk', validationObject)}</AppText>
                    <AdvanceSelect options={locations} initValue={values.locations_box_fk} onChange={(val) => setFieldValue('locations_box_fk', val?.id ?? null)} isError={touched.locations_box_fk && errors.locations_box_fk}/>
                    <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error} mediumFont>{touched.locations_box_fk && errors.locations_box_fk ? errors.locations_box_fk : ''}</AppText>
                </VStack>
            }

            <VStack space={'0.2rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('weeklyFrequency')}{checkRequired('session_per_week', validationObject)}</AppText>
                <SimpleInput value={values.session_per_week}
                                max={20}
                                min={1}
                                onChangeCallback={(val) => setFieldValue('session_per_week', val)}
                                width={'4rem'}
                                numericOnly={true}
                                status={errors.session_per_week && touched.session_per_week ? 'error' : null}
                />
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error} mediumFont>{touched.session_per_week && errors.session_per_week ? errors.session_per_week : ''}</AppText>
            </VStack>
        </VStack>
    );
};

export default TrackDetails;
